import type { Breadcrumb } from '@sentry/react';
import { pong } from 'features/application/actions';
import { showHoverChildrenChanged } from 'features/layout/features/config/configSlice';
import { contentWidthChanged } from 'features/layout/features/contentDimensions/contentDimensionsSlice';
import { activeSpeakerChanged, speakerStoppedTalking } from 'features/streaming/actions';
import {
  indicatorAverageMOSChanged,
  statsTickPublished,
} from 'features/system-stats/systemStatsSlice';
import type { SignalingReceivedEventType } from 'services/signaling/types';
import { redactActionIceServers } from 'utils/sentry/redactIceServers';

// Shared by Redux DevTools and Sentry. Periodic or bursty actions would otherwise push the
// diagnostic breadcrumbs out of Sentry's buffer; rtcstats keeps the stats ticks.
export const NOISY_ACTION_TYPES: readonly string[] = [
  statsTickPublished.type,
  indicatorAverageMOSChanged.type,
  pong.type,
  activeSpeakerChanged.type,
  speakerStoppedTalking.type,
  contentWidthChanged.type,
  showHoverChildrenChanged.type,
];

// Dropped from both the Redux and the signaling-message breadcrumbs: ~25/min in a busy call,
// so even one copy fills the buffer in ~20 min.
const SPEAKER_SIGNALING_EVENTS = new Set<unknown>([
  'activeSpeakerChanged',
  'speakerStoppedTalking',
] satisfies SignalingReceivedEventType[]);

// janus.js polls the session for events with ?rid=…&maxev=…; every successful poll is routine.
const JANUS_LONG_POLL_QUERY = /[?&]maxev=/;
const FIRST_HTTP_ERROR_STATUS = 400;

export const toReduxBreadcrumb = <A extends { type: string; payload?: unknown }>(
  action: A
): A | null => (NOISY_ACTION_TYPES.includes(action.type) ? null : redactActionIceServers(action));

const isSuccessfulJanusLongPoll = ({ category, data }: Breadcrumb) =>
  category === 'fetch' &&
  data?.method === 'GET' &&
  typeof data.status_code === 'number' &&
  data.status_code < FIRST_HTTP_ERROR_STATUS &&
  typeof data.url === 'string' &&
  JANUS_LONG_POLL_QUERY.test(data.url);

const isSpeakerSignalingMessage = ({ category, data }: Breadcrumb) =>
  category === 'streaming' && SPEAKER_SIGNALING_EVENTS.has(data?.event);

export const dropRoutineBreadcrumb = (breadcrumb: Breadcrumb): Breadcrumb | null =>
  isSuccessfulJanusLongPoll(breadcrumb) || isSpeakerSignalingMessage(breadcrumb)
    ? null
    : breadcrumb;
